// Missões diárias: 4 por dia (trocam à meia-noite de Brasília), prêmio em Pokédollar.
import { api, errorMessage, type MissionDto, type MissionsResponse } from "../api";
import { play } from "../audio/sfx";
import { traitName } from "../data";
import { num, t, type MsgKey } from "../i18n";
import type { View } from "../router";
import { loadSession } from "../session";
import { esc, screen } from "../ui";
import { PDX } from "./market";

/** Texto da missão no idioma atual. */
export function missionText(m: MissionDto): string {
  if (m.kind === "synergy") return t("missions.k.synergy", { n: m.count ?? 0, trait: traitName(m.trait ?? "") });
  if (m.kind === "items" || m.kind === "level") return t(`missions.k.${m.kind}` as MsgKey, { n: m.need ?? 0 });
  return t(`missions.k.${m.kind}` as MsgKey, { n: m.target });
}

const ICON: Record<string, string> = {
  synergy: "🔗", play: "🎮", win: "🏆", tophalf: "🥈", "ranked-play": "⚔️", "ranked-win": "👑",
  "pvp-wins": "🥊", star3: "⭐", items: "🎒", level: "📈",
};

export const missionsView: View = (root) => {
  root.innerHTML = screen(t("missions.title"), "/menu", `<div class="bw-textbox">${t("common.loading")}</div>`);
  const body = root.querySelector<HTMLElement>(".screen__body")!;
  let data: MissionsResponse | null = null;
  let tick = 0;

  const fmtLeft = (ms: number) => {
    const m = Math.max(0, Math.ceil(ms / 60000));
    return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
  };

  function render(alert?: { msg: string; kind: string }) {
    const d = data;
    if (!d) return;
    const ready = d.missions.filter((m) => !m.claimed && m.progress >= m.target).length;
    body.innerHTML = `
      <section class="bw-panel streak-card">
        <div class="streak-fire">🔥<b>${d.streak.streak}</b></div>
        <div>
          <b>${t("missions.streak", { n: d.streak.streak })}</b><br>
          <span class="bw-dim small">${t("missions.best", { n: d.streak.best })} · ${t("missions.completedTotal", { n: num(d.completed) })}</span>
        </div>
        <a class="bw-btn" href="#/ranking?aba=streak">${t("missions.seeRanking")}</a>
      </section>
      ${alert ? `<div class="bw-alert bw-alert--${alert.kind}">${esc(alert.msg)}</div>` : ""}
      <div class="bw-textbox">${t("missions.intro")} <b data-left>${t("missions.resetsIn", { t: fmtLeft(d.resetsAt - Date.now()) })}</b></div>
      <ul class="missions">${d.missions.map((m) => {
        const done = m.progress >= m.target;
        const pct = Math.round((m.progress / m.target) * 100);
        return `<li class="bw-panel mission ${m.claimed ? "claimed" : done ? "ready" : ""}">
          <span class="mission-icon" aria-hidden="true">${ICON[m.mission.kind] ?? "★"}</span>
          <div class="mission-main">
            <b>${esc(missionText(m.mission))}</b>
            <div class="pet-bar__track"><div class="pet-bar__fill" style="width:${pct}%"></div></div>
            <span class="bw-dim small">${m.progress}/${m.target}</span>
          </div>
          ${m.claimed
            ? `<span class="bw-dim">✔ ${t("missions.claimed")}</span>`
            : `<button class="bw-btn ${done ? "bw-btn--gold" : ""}" data-claim="${m.slot}" ${done ? "" : 'aria-disabled="true"'}>${PDX} ${m.reward}</button>`}
        </li>`;
      }).join("")}</ul>
      ${ready ? "" : `<p class="bw-dim small">${t("missions.hint")}</p>`}`;
  }

  async function load(alert?: { msg: string; kind: string }) {
    try {
      data = await api.missions();
      render(alert);
    } catch (e) {
      body.innerHTML = `<div class="bw-alert bw-alert--error">${esc(errorMessage(e))}</div>`;
    }
  }

  body.addEventListener("click", async (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-claim]");
    if (!el || el.getAttribute("aria-disabled") === "true" || el.getAttribute("aria-busy") === "true") return;
    el.setAttribute("aria-busy", "true");
    try {
      const r = await api.claimMission(Number(el.dataset.claim));
      play("reward");
      void loadSession();
      await load({ msg: t("missions.got", { n: r.reward }), kind: "ok" });
    } catch (err) {
      await load({ msg: errorMessage(err), kind: "error" });
    }
  });

  tick = window.setInterval(() => {
    const el = body.querySelector("[data-left]");
    if (!data || !el) return;
    if (Date.now() >= data.resetsAt) void load();
    else el.textContent = t("missions.resetsIn", { t: fmtLeft(data.resetsAt - Date.now()) });
  }, 30_000);
  void load();
  return () => clearInterval(tick);
};
